import { useMemo, useRef, useState } from 'react';
import {
  Box, Card, Typography, Button, Grid, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Tooltip,
} from '@mui/material';
import { Savings, FileDownload, Edit, DeleteForever } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api/client';
import { DataTable, type DataTableColumn } from '../../components/common/DataTable';
import { PageHeader } from '../../components/common/PageHeader';
import { SelectField } from '../../components/fields/SelectField';
import { useAuthStore } from '../../store/auth.store';
import { exportToCsv } from '../../utils/exportToCsv';
import { MaterialPurchaseDialog } from './MaterialPurchaseDialog';
import { DeleteMaterialPurchaseDialog } from './DeleteMaterialPurchaseDialog';
import {
  defaultFilters, RegisterFilters, SUPPORTED_MATERIALS, PURCHASE_TYPES, purchaseTypeLabel, MATERIAL_PURCHASE_CSV_COLUMNS, buildListParams, materialLabel, purchaseDetailItems,
} from './materialPurchaseRegister';

const fmt = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const mono = { fontFamily: 'monospace' } as const;

// Material Purchase register (user-facing name for the OldGoldExchange rows that carry a tested-purity snapshot).
// New purchases go through the one existing MaterialPurchaseDialog -> createMaterialPurchase(); this page only lists,
// filters, inspects (read-only) and exports. Every figure is the server's stored value.
export function MaterialPurchaseTable({ rows, onOpen, onEdit, onDelete }: {
  rows: any[]; onOpen: (x: any) => void; onEdit?: (x: any) => void; onDelete?: (x: any) => void;
}) {
  const hasActions = !!(onEdit || onDelete);
  const when = (x: any) => new Date(x.createdAt ?? x.exchangeDate).toLocaleString('en-IN');
  const num = (v: any) => Number(v);
  // Shared DataTable: drag headers to reorder, right-click to hide / auto-fit, per-column filters, sort, Export CSV.
  const columns: DataTableColumn[] = [
    { key: 'when', label: 'Date & Time', width: 150, render: (x) => <span style={{ fontSize: 12 }}>{when(x)}</span>, filterValue: when, sortValue: (x) => new Date(x.createdAt ?? x.exchangeDate).getTime() },
    { key: 'no', label: 'Purchase No.', width: 130, render: (x) => <span style={{ ...mono, fontWeight: 700, fontSize: 12 }}>{x.exchangeNumber}</span>, filterValue: (x) => x.exchangeNumber },
    { key: 'customer', label: 'Customer', render: (x) => x.client?.name, filterValue: (x) => x.client?.name ?? '' },
    { key: 'material', label: 'Material', width: 100, render: (x) => materialLabel(x.metalType), filterValue: (x) => materialLabel(x.metalType) },
    { key: 'type', label: 'Purchase Type', width: 120, render: (x) => purchaseTypeLabel(x), filterValue: (x) => purchaseTypeLabel(x) },
    { key: 'gross', label: 'Gross Weight', align: 'right', width: 120, render: (x) => <span style={mono}>{num(x.grossWeight).toFixed(3)} g</span>, filterValue: (x) => `${num(x.grossWeight).toFixed(3)} g`, sortValue: (x) => num(x.grossWeight) },
    { key: 'purity', label: 'Tested Purity', align: 'right', width: 120, render: (x) => <span style={mono}>{num(x.testedPurityPercent).toFixed(2)}%</span>, filterValue: (x) => `${num(x.testedPurityPercent).toFixed(2)}%`, sortValue: (x) => num(x.testedPurityPercent) },
    { key: 'fine', label: 'Fine Weight', align: 'right', width: 120, render: (x) => <span style={mono}>{num(x.fineWeight).toFixed(4)} g</span>, filterValue: (x) => `${num(x.fineWeight).toFixed(4)} g`, sortValue: (x) => num(x.fineWeight) },
    { key: 'base', label: 'Base Rate', align: 'right', width: 120, render: (x) => <span style={mono}>{fmt(num(x.baseRate24K))}</span>, filterValue: (x) => fmt(num(x.baseRate24K)), sortValue: (x) => num(x.baseRate24K) },
    { key: 'eff', label: 'Effective Rate', align: 'right', width: 130, render: (x) => <span style={mono}>{fmt(num(x.effectivePurchaseRate))}</span>, filterValue: (x) => fmt(num(x.effectivePurchaseRate)), sortValue: (x) => num(x.effectivePurchaseRate) },
    { key: 'value', label: 'Purchase Value', align: 'right', width: 140, render: (x) => <span style={{ ...mono, fontWeight: 700 }}>{fmt(num(x.valuedAmount))}</span>, filterValue: (x) => fmt(num(x.valuedAmount)), sortValue: (x) => num(x.valuedAmount) },
    {
      key: 'credit', label: 'Store Credit', align: 'right', width: 130,
      render: (x) => <span style={{ ...mono, color: x.reversedAt ? 'gray' : 'green' }}>{x.reversedAt ? 'Reversed' : fmt(num(x.creditCreated ?? x.valuedAmount))}</span>,
      filterValue: (x) => (x.reversedAt ? 'Reversed' : fmt(num(x.creditCreated ?? x.valuedAmount))), sortValue: (x) => (x.reversedAt ? 0 : num(x.creditCreated ?? x.valuedAmount)),
    },
    { key: 'branch', label: 'Branch', width: 130, render: (x) => x.branch?.name, filterValue: (x) => x.branch?.name ?? '' },
    { key: 'by', label: 'Created By', width: 130, render: (x) => x.createdByName ?? '—', filterValue: (x) => x.createdByName ?? '—' },
    ...(hasActions ? [{
      key: 'actions', label: 'Actions', align: 'center' as const, width: 100, hideable: false, filterable: false,
      render: (x: any) => (
        <span style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
          {!x.reversedAt && onEdit && (
            <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${x.exchangeNumber}`} onClick={() => onEdit(x)}><Edit fontSize="small" /></IconButton></Tooltip>
          )}
          {!x.reversedAt && onDelete && (
            <Tooltip title="Delete"><IconButton size="small" color="error" aria-label={`Delete ${x.exchangeNumber}`} onClick={() => onDelete(x)}><DeleteForever fontSize="small" /></IconButton></Tooltip>
          )}
        </span>
      ),
    }] : []),
  ];
  return (
    <Card>
      <DataTable tableId="material-purchase-register" columns={columns} rows={rows} isLoading={false} sortable csvFileName="material-purchases"
        getRowId={(x) => x.id} onRowClick={(x) => onOpen(x)} emptyLabel="No material purchases found" />
    </Card>
  );
}

export function MaterialPurchaseDetails({ purchase, onClose }: { purchase: any | null; onClose: () => void }) {
  const x = purchase;
  const items = x ? purchaseDetailItems(x) : [];
  return (
    <Dialog open={!!x} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Material Purchase {x?.exchangeNumber}</DialogTitle>
      <DialogContent>
        {x?.reversedAt && (
          <Chip color="warning" size="small" sx={{ mb: 1 }}
            label={`Reversed ${new Date(x.reversedAt).toLocaleString('en-IN')}${x.reversalReason ? ` — ${x.reversalReason}` : ''}`} />
        )}
        <Grid container spacing={1}>
          {items.map(([k, v]) => (
            <Grid item xs={12} sm={6} key={k}>
              <Typography variant="caption" color="text.secondary">{k}</Typography>
              <Typography variant="body2" sx={mono}>{v}</Typography>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

export function OldGoldExchange() {
  // Same gates as before: the page needs sales:view (route guard); buying needs invoice:create (POST permission).
  const canRecord = useAuthStore((s) => s.hasPermission('invoice:create'));
  const [filters, setFilters] = useState<RegisterFilters>(defaultFilters);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  // Edit / Delete are their own permissions (assigned in Roles & Permissions); the server re-checks both.
  const canEdit = useAuthStore((s) => s.hasPermission('materialPurchase:edit'));
  const canDelete = useAuthStore((s) => s.hasPermission('materialPurchase:delete'));
  const [editing, setEditing] = useState<any | null>(null);
  const [deleting, setDeleting] = useState<any | null>(null);
  const setF = (k: keyof RegisterFilters, v: string) => setFilters((p) => ({ ...p, [k]: v }));

  const params = buildListParams(filters);
  const { data } = useQuery({
    queryKey: ['material-purchases', params],
    queryFn: () => apiGet<any>('/sales/material-purchases', params),
  });
  const rows: any[] = (data as any)?.data ?? data ?? [];

  const { data: clients } = useQuery({ queryKey: ['clients-list'], queryFn: () => apiGet<any>('/orders/clients') });
  const clientList: any[] = (clients as any)?.clients ?? (clients as any)?.data ?? clients ?? [];
  const { data: branches } = useQuery({ queryKey: ['sales-branches'], queryFn: () => apiGet<any[]>('/sales/branches') });
  const branchList: any[] = (branches as any)?.data ?? branches ?? [];

  // Creators seen so far (a filtered list would otherwise shrink its own option list).
  const creators = useRef(new Map<string, string>());
  rows.forEach((r) => { if (r.createdById && r.createdByName) creators.current.set(r.createdById, r.createdByName); });
  const creatorOptions = useMemo(
    () => [...creators.current.entries()].map(([value, label]) => ({ value, label })),
    [rows], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box>
      <PageHeader title="Material Purchase" subtitle="Material Purchase Register — history" actions={canRecord ? [
        { label: '+ New Material Purchase', icon: <Savings />, onClick: () => setPurchaseOpen(true) },
      ] : undefined} />
      {/* The one existing purchase dialog; it invalidates ['material-purchases'] itself, which refreshes this register. */}
      <MaterialPurchaseDialog open={purchaseOpen} onClose={() => setPurchaseOpen(false)} />
      <MaterialPurchaseDialog open={!!editing} editing={editing} onClose={() => setEditing(null)} />
      <DeleteMaterialPurchaseDialog purchase={deleting} onClose={() => setDeleting(null)} />
      <MaterialPurchaseDetails purchase={selected} onClose={() => setSelected(null)} />

      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Date From" value={filters.from}
              InputLabelProps={{ shrink: true }} onChange={(e) => setF('from', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Date To" value={filters.to}
              InputLabelProps={{ shrink: true }} onChange={(e) => setF('to', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SelectField size="small" label="Customer" value={filters.clientId} emptyLabel="All"
              onChange={(e) => setF('clientId', e.target.value)}
              options={clientList.map((c: any) => ({ value: c.id, label: `${c.clientCode} — ${c.name}` }))} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SelectField size="small" label="Material" value={filters.metalType} emptyLabel="All"
              onChange={(e) => setF('metalType', e.target.value)} options={SUPPORTED_MATERIALS} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SelectField size="small" label="Purchase Type" value={filters.purchaseType} emptyLabel="All"
              onChange={(e) => setF('purchaseType', e.target.value)} options={PURCHASE_TYPES} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SelectField size="small" label="Branch" value={filters.branchId} emptyLabel="All"
              onChange={(e) => setF('branchId', e.target.value)}
              options={branchList.map((b: any) => ({ value: b.id, label: b.name }))} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Purchase No." value={filters.exchangeNumber}
              InputLabelProps={{ shrink: true }} onChange={(e) => setF('exchangeNumber', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SelectField size="small" label="Created By" value={filters.createdById} emptyLabel="All"
              onChange={(e) => setF('createdById', e.target.value)} options={creatorOptions} />
          </Grid>
          <Grid item xs="auto">
            <Button size="small" onClick={() => setFilters(defaultFilters())}>Clear</Button>
          </Grid>
          <Grid item xs="auto">
            <Button size="small" variant="outlined" startIcon={<FileDownload />} disabled={rows.length === 0}
              onClick={() => exportToCsv('material-purchases', MATERIAL_PURCHASE_CSV_COLUMNS, rows)}>
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>Material Purchase History</Typography>
      <MaterialPurchaseTable rows={rows} onOpen={setSelected} onEdit={canEdit ? setEditing : undefined} onDelete={canDelete ? setDeleting : undefined} />
    </Box>
  );
}
